import dayjs from "dayjs";
import { ColDef } from "ag-grid-community";
import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { FormEvent, useEffect, useState } from "react";
import { Sensor } from "../../common/types/sensor";
import { request } from "../../common/api";
import { SensorFile } from "../../common/types/sensorFile";
import { fileDownload } from "../../common/functions/download";
import { currentShipState } from "../../common/states/ship";
import useEquipmentInfoSearchInputData from "../../common/hooks/useEquipmentInfoSearchInputData";
import {
  GET_EQUIPMENT_INFO,
  GET_EQUIPMENT_INFO_FILE,
} from "../../common/query-keys";
import { formatDisplay, formatFixedPoint } from "../../common/functions/format";
import { Grid } from "../../components/molecules/Grid";
import { Buttons } from "../../components/atoms/Buttons";
import { SearchBar } from "../../components/molecules/SearchBar";
import { InputText } from "../../components/atoms/Inputs/InputText";
import { InputSelect } from "../../components/atoms/Inputs/InputSelect";
import { PageContent } from "../../components/organisms/Contents/PageContent";
import { confirmDialog } from "../../components/containers/ConfirmDialog";
import { AddEquipmentInfoModal } from "../../components/containers/Equipment/AddEquipmentInfoModal";
import { ReactComponent as IconDownload } from "../../styles/assets/svg/icon_download_black.svg";

export interface EquipmentInfoSearchInputs {
  ssrShipTypeNo?: string;
  ssrShipNo?: string;
  ssrMcyName?: string;
  ssrMachine?: string;
  ssrLowMcyName?: string;
  ssrName?: string;
}

export interface EquipmentInfoUpperGridSearchInputs {
  ssrShipTypeNo?: string;
  ssrShipNo?: string;
}

export const EquipmentInfoPage = () => {
  const currentShip = useRecoilValue(currentShipState);

  const searchDefaultValue = {
    ssrShipTypeNo: currentShip.shipTypeNo,
    ssrShipNo: currentShip.shipNo,
    ssrMcyName: "",
    ssrMachine: "",
    ssrLowMcyName: "",
    ssrName: "",
  };

  const [searchInputs, setSearchInputs] = useState<EquipmentInfoSearchInputs>();
  const [upperGridSearch, setUpperGridSearch] =
    useState<EquipmentInfoUpperGridSearchInputs>({
      ssrShipNo: currentShip.shipNo,
      ssrShipTypeNo: currentShip.shipTypeNo,
    });

  const [modalShow, setModalShow] = useState(false);

  const {
    search,
    machinerySelectOptions,
    lowMachinerySelectOptions,
    handleMcySelectChange,
    handleLowMcySelectChange,
    handleValueChange,
  } = useEquipmentInfoSearchInputData(searchDefaultValue);

  /** 상단 Grid 함정 정보 파일 조회 */
  const { data: uppderGridData, refetch: upperGridDataRefetch } = useQuery({
    queryKey: [GET_EQUIPMENT_INFO_FILE],
    queryFn: async () => {
      return await request<SensorFile[]>({
        method: "GET",
        url: "/equipment-info/files",
        params: upperGridSearch,
      });
    },
  });

  /** 하단 Grid 함정 정보 파일 조회 */
  const { data: lowerGridData, refetch: lowerGridDataRefetch } = useQuery({
    queryKey: [GET_EQUIPMENT_INFO, searchInputs],
    queryFn: async () => {
      return await request<Sensor[]>({
        method: "GET",
        url: "/equipment-info",
        params: searchInputs,
      });
    },
    select: (data) => {
      if (searchInputs?.ssrName !== undefined) {
        const result: Sensor[] = data.filter((item: Sensor) => {
          if (
            searchInputs?.ssrName !== undefined &&
            (item.ssrMachineName?.includes(searchInputs.ssrName) ||
              item.ssrEquipment?.includes(searchInputs.ssrName) ||
              item.ssrName?.includes(searchInputs.ssrName) ||
              item.ssrCode?.includes(searchInputs.ssrName) ||
              item.ssrSignal === searchInputs.ssrName ||
              item.ssrType?.includes(searchInputs.ssrName) ||
              item.ssrSupplier?.includes(searchInputs.ssrName) ||
              item.ssrAlarm === searchInputs.ssrName ||
              item.ssrLl === searchInputs.ssrName ||
              item.ssrL === searchInputs.ssrName ||
              item.ssrH === searchInputs.ssrName ||
              item.ssrHh === searchInputs.ssrName ||
              item.ssrMax === searchInputs.ssrName ||
              item.ssrUnit?.includes(searchInputs.ssrName) ||
              item.ssrDescription?.includes(searchInputs.ssrName))
          ) {
            return item;
          }
        });
        return result;
      } else {
        return data;
      }
    },
    enabled: false,
  });
  /** 함정 장비 정보 파일(IO List) 파일 다운로드 함수 */
  const onDownloadHandler = async (e: any) => {
    {
      const isConfirm = await confirmDialog({
        text: "해당 항목을 다운받으시겠습니까?",
      });

      if (isConfirm) {
        excelDownloads({
          fileName: e.data.mcyFFileOriginalName,
          filePath: e.data.mcyFFilename,
        });
      }
    }
  };
  const onLowerSearchHandler = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log(search);
    if (search.ssrMcyName === "장비명") {
      search.ssrMcyName = "";
    }
    setSearchInputs(search);
  };

  useEffect(() => {
    if (upperGridSearch) {
      upperGridDataRefetch();
      lowerGridDataRefetch();
    }

    if (searchInputs) {
      lowerGridDataRefetch();
    }
  }, [upperGridSearch, searchInputs]);

  const excelDownloads = async (data: {
    fileName: string;
    filePath: string;
  }) => {
    try {
      await request<Blob>({
        method: "POST",
        url: `/equipment-info/excel/downloads`,
        data: data,
        responseType: "blob",
      })
        .then((res: any) => {
          // 반환값이 배열 빈값이라면 다운로드 미실행
          if (res.length !== 0) fileDownload(res, data.fileName);
        })
        .catch((e) => {
          console.log(e);
        });
    } catch (e) {
      console.log(e);
    }
  };

  /** 파일 다운로드 버튼 */
  const downloadButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconDownload />}
        onClick={async () => onDownloadHandler(e)}
      />
    );
  };

  /** 샘플 파일 다운로드 */
  const onSampleFileDownloadHanlder = async () => {
    const fileName = "equipment_info_sample.xlsx";
    const response = await request<Blob>({
      url: `/files/excel/sample/${fileName}`,
      method: "POST",
      responseType: "blob",
    });

    fileDownload(response, fileName);
  };

  const upperColumnDefs: ColDef[] = [
    {
      headerName: "파일명",
      field: "mcyFFileOriginalName",
      flex: 1,
    },
    {
      headerName: "설명",
      field: "mcyFMemo",
    },
    {
      headerName: "등록일",
      field: "userTime",
      valueGetter: (params: any) =>
        dayjs(params.data.userTime).format("YYYY-MM-DD"),
    },
    {
      headerName: "등록자",
      field: "userName",
    },
    {
      headerName: "다운로드",
      cellRenderer: downloadButtonRender,
    },
  ];

  const lowerColumnDefs = [
    {
      type: "string",
      headerName: "장비명",
      field: "ssrMachineName",
      width: 120,
    },
    {
      type: "string",
      headerName: "센서코드",
      field: "ssrCode",
      width: 150,
    },
    {
      headerName: "센서명",
      field: "ssrName",
      width: 260,
    },
    {
      headerName: "신호",
      field: "ssrSignal",
      width: 80,
    },
    {
      headerName: "센서 값 타입",
      field: "ssrType",
      width: 90,
    },
    {
      headerName: "공급대상",
      field: "ssrSupplier",
      width: 80,
    },
    {
      headerName: "계통",
      field: "ssrEquipment",
      width: 120,
    },
    {
      headerName: "알람 센서 유/무",
      field: "ssrAlarm",
      width: 120,
    },
    {
      headerName: "임계값",
      children: [
        {
          headerName: "LL",
          field: "ssrLl",
          width: 80,
          valueGetter: (params: any) => formatDisplay(params.data.ssrMax),
        },
        {
          headerName: "L",
          field: "ssrL",
          width: 80,
          valueGetter: (params: any) => formatDisplay(params.data.ssrMax),
        },
        {
          headerName: "H",
          field: "ssrH",
          width: 80,
          valueGetter: (params: any) => formatDisplay(params.data.ssrMax),
        },
        {
          headerName: "HH",
          field: "ssrHh",
          width: 80,
          valueGetter: (params: any) => formatDisplay(params.data.ssrMax),
        },
      ],
    },
    {
      headerName: "센서 범위",
      children: [
        {
          headerName: "Min",
          field: "ssrMin",
          width: 80,
          valueGetter: (params: any) => formatFixedPoint(params.data.ssrMin),
        },
        {
          headerName: "Max",
          field: "ssrMax",
          width: 80,
          valueGetter: (params: any) => formatFixedPoint(params.data.ssrMax),
        },
      ],
    },
    {
      headerName: "단위",
      field: "ssrUnit",
      width: 80,
      valueGetter: (params: any) => formatFixedPoint(params.data.ssrUnit),
    },
    {
      headerName: "설명",
      field: "ssrDescription",
      width: 140,
    },
    {
      headerName: "진단 대상",
      field: "ssrDiagnosisTarget",
      width: 80,
    },
  ];

  return (
    <>
      <PageContent
        depth01Title="관리자 설정"
        depth02Title="함정 관리"
        depth03Title="장비 정보관리"
      >
        <>
          <SearchBar
            gridButtons={
              <>
                <Buttons
                  type="button"
                  size="md"
                  layout="secondary"
                  label="샘플 파일"
                  onClick={onSampleFileDownloadHanlder}
                />
                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="신규 등록"
                  onClick={() => setModalShow(!modalShow)}
                />
              </>
            }
          />
          <Grid
            gridTitle="함정 장비 정보 파일(IO List)"
            rowData={uppderGridData || []}
            columnDefs={upperColumnDefs}
            pagingShow={false}
            height={100}
          />

          <SearchBar
            onSearch={onLowerSearchHandler}
            searchTitle="장비 정보 조회"
            searchInput={
              <>
                <InputSelect
                  name="ssrMachine"
                  size="md"
                  width={120}
                  placeholder="장비명"
                  options={machinerySelectOptions}
                  onChange={handleMcySelectChange}
                  disabled={!search.ssrShipNo}
                />
                <InputSelect
                  name="ssrLowMcyName"
                  size="md"
                  width={120}
                  placeholder="계통"
                  options={lowMachinerySelectOptions}
                  onChange={handleLowMcySelectChange}
                  disabled={!search.ssrMachine}
                />
                <InputText
                  name="ssrName"
                  size="md"
                  placeholder="검색"
                  width={180}
                  onChange={handleValueChange}
                />
              </>
            }
          />
          <Grid
            rowData={lowerGridData || []}
            columnDefs={lowerColumnDefs}
            defaultPageSize={10}
          />
        </>
      </PageContent>
      {modalShow && (
        <AddEquipmentInfoModal
          onCloseModal={() => setModalShow(false)}
          upperGridSearch={upperGridSearch}
        />
      )}
    </>
  );
};
